import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { DeferredContent } from "@impero-com/common/components/deferredContent";
import { AsyncTableFilter } from "@impero-com/common/components/filter/asyncFilter";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
    ExtractorExpander,
    getAllExtractors,
    Group,
    NodeDescriptor,
} from "@impero-com/common/utils/dataView";
import {
    getDefaultEndDate,
    getDefaultStartDate,
} from "@impero-com/common/utils/date";
import {
    formatDateTimeAndTimezone,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    AsyncDataViewIndex,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { ContentWithoutBorder } from "@impero-com/ui";
import { Skeleton } from "antd";
import moment from "moment-timezone";
import { useNavigate, useLocation } from "react-router-dom";

import {
    UserEventResourceType,
    UserEventRelatedResourceView,
    UserView,
    IanaTimezoneView,
} from "api/generated/model";
import {
    AggregateColumnBuilder,
    ColumnBuilder,
    ColumnOptions,
    TableDataViewer,
    toColumn,
} from "components/dataViewer";
import {
    AggregatesConfiguration,
    DataViewerSettings,
} from "components/dataViewer/settings";
import { DateFilterSelector, setDateFilter } from "components/dateFilter";
import { DateFilter, DueDateFilter } from "components/dateFilter/types";
import { DateFormatter } from "components/dateFormatter";
import {
    eventSourceLabel,
    eventSourceTag,
} from "components/eventSourceTag/eventSourceTag";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { newExportMetadata } from "utils/export";
import { useOrgUserProfileCookie } from "utils/profileCookie";

import { LegacyRoute } from "../../legacyRoutes";
import { WebRoute } from "../../routes";

import {
    EVENT_LOG_COLUMN,
    EVENT_LOG_PARAM,
    parseEventLogDate,
    parseEventLogFilter,
} from "./eventLogUrl";
import { useUserEventsQuery } from "./queries/useUserEventsQuery";
import * as theme from "./theme.css";
import * as rp from "./types";
import { formatUserEventReact, formatUserEventString } from "./userEvents";

/**
 * This component is responsible for the URL management,
 * and delegates the loading and rendering of the user events to the AsyncEventLogInner component.
 */
interface AsyncEventLogInnerProps {
    startDate: moment.Moment;
    endDate: moment.Moment;
    filter: SimpleAndFilter | null;
    tableSettings: DataViewerSettings | null;
    updateFilter: (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
    removeFilter: (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
    removeAllFilters: () => void;
    reloadEventsBasedOnDate: (dateFilter: DateFilter) => void;
    reloadEventsBasedOnSettings: (settings: DataViewerSettings) => void;
}
export function AsyncEventLog() {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);

    const startDateParam = searchParams.get(EVENT_LOG_PARAM.START_DATE);
    const endDateParam = searchParams.get(EVENT_LOG_PARAM.END_DATE);

    const startDate = startDateParam
        ? parseEventLogDate(startDateParam)
        : getDefaultStartDate(moment());
    const endDate = endDateParam
        ? parseEventLogDate(endDateParam)
        : getDefaultEndDate();

    const filterParam = searchParams.get(EVENT_LOG_PARAM.FILTER);

    const filter = useMemo(
        () => parseEventLogFilter(filterParam),
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
        [filterParam]
    );

    const tableSettingsParam = searchParams.get("tableSettings") ?? undefined;
    const tableSettings = getTableSettings(tableSettingsParam);

    const redirectToUpdatedFilterUrl = (filter: SimpleAndFilter | null) => {
        const searchParamsWithFilter = new URLSearchParams(location.search);
        const filterSearchString =
            filter !== null ? JSON.stringify(filter) : "";
        searchParamsWithFilter.set(EVENT_LOG_PARAM.FILTER, filterSearchString);
        navigate(`${location.pathname}?${searchParamsWithFilter}`);
    };

    const removeAllFilters = () => {
        const searchParamsWithFilter = new URLSearchParams(location.search);
        searchParamsWithFilter.set(EVENT_LOG_PARAM.FILTER, "");
        navigate(`${location.pathname}?${searchParamsWithFilter}`);
    };

    const updateFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const searchParamsWithFilter = new URLSearchParams(location.search);
        const oldFilter = parseEventLogFilter(
            searchParamsWithFilter.get(EVENT_LOG_PARAM.FILTER)
        );
        const filter = addFieldFilter(
            extractorId,
            fieldFilter,
            excluded,
            oldFilter
        );
        redirectToUpdatedFilterUrl(filter);
    };

    const removeFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const searchParamsWithFilter = new URLSearchParams(location.search);
        const oldFilter = parseEventLogFilter(
            searchParamsWithFilter.get(EVENT_LOG_PARAM.FILTER)
        );
        if (oldFilter) {
            const filter = removeFieldFilter(
                extractorId,
                fieldFilter,
                excluded,
                oldFilter
            );
            redirectToUpdatedFilterUrl(filter);
        }
    };

    const reloadEventsBasedOnDate = (dateFilter: DateFilter) => {
        const urlWithDateFilter = setDateFilter(dateFilter);
        navigate(urlWithDateFilter.pathname + urlWithDateFilter.search);
    };

    const reloadEventsBasedOnSettings = (settings: DataViewerSettings) => {
        const searchParamsWithGrouping = new URLSearchParams(location.search);
        searchParamsWithGrouping.set("tableSettings", JSON.stringify(settings));
        navigate(`${location.pathname}?${searchParamsWithGrouping}`);
    };

    return (
        <AsyncEventLogInner
            startDate={startDate}
            endDate={endDate}
            filter={filter}
            tableSettings={tableSettings}
            updateFilter={updateFilter}
            removeFilter={removeFilter}
            removeAllFilters={removeAllFilters}
            reloadEventsBasedOnDate={reloadEventsBasedOnDate}
            reloadEventsBasedOnSettings={reloadEventsBasedOnSettings}
        />
    );
}

/**
 * This component is responsible for loading the user events and displaying them in a table.
 */
const AsyncEventLogInner = ({
    startDate,
    endDate,
    filter,
    tableSettings,
    updateFilter,
    removeFilter,
    removeAllFilters,
    reloadEventsBasedOnDate,
    reloadEventsBasedOnSettings,
}: AsyncEventLogInnerProps) => {
    const { data, hasNextPage, isFetching, fetchNextPage } = useUserEventsQuery(
        startDate.unix(),
        endDate.unix()
    );

    // Force the fetch of the whole dataset
    if (hasNextPage && !isFetching) {
        void fetchNextPage();
    }

    const orgProfileCookie = useOrgUserProfileCookie();

    const dateFilter = getDateFilter(startDate, endDate);

    const dataView = useMemo(
        () =>
            buildDataView(data?.events ?? [], data?.users ?? {}, {
                name: orgProfileCookie?.timezone || "Europe/Copenhagen",
            }),
        [data, orgProfileCookie]
    );
    const expanders = useMemo(() => buildDataViewExpanders(), []);
    const [dataViewIndex, setDataViewIndex] = useState(
        () => new AsyncDataViewIndex(dataView, undefined, expanders)
    );
    const inserted = data?.lastPageEventIds;

    const dateRangeKey = `${startDate.unix()}-${endDate.unix()}`;
    const dateRangeKeyRef = useRef(dateRangeKey);
    useEffect(() => {
        setDataViewIndex((dataViewIndex) => {
            // Selects the new dataViewIndex based on date range key.
            if (dateRangeKeyRef.current !== dateRangeKey) {
                // date range key has changed,
                // we should trash the old dataViewIndex (and stop indexing) instead of copying it.
                dateRangeKeyRef.current = dateRangeKey;
                dataViewIndex.unload();
                return new AsyncDataViewIndex(dataView, undefined, expanders);
            } else {
                // date range key is the same, we can copy the old dataViewIndex and update the dataViewIndex.
                return new AsyncDataViewIndex(
                    dataViewIndex,
                    dataView,
                    { inserted },
                    expanders
                );
            }
        });
    }, [dataView, expanders, inserted, dateRangeKey]);

    const queryResult = useMemo(() => {
        if (data) {
            return buildQueryResult(dataView, dataViewIndex, filter);
        } else {
            return buildEmptyQueryResult(dataView);
        }
    }, [dataView, dataViewIndex, filter, data]);

    const availableAggregates = {
        aggregates: [
            {
                extractorId: EVENT_LOG_COLUMN.ID,
                aggregateId: "count",
                aggregateLabel: t("EventLogPage_Aggregate_Count"),
                countValue: () => 1,
            },
        ],
        dimensions: [
            {
                id: "count",
                label: t("EventLogPage_Aggregate_Count"),
                aggregateIds: ["count"],
            },
        ],
    };
    const defaultAggregates = {
        type: "Dimension" as const,
        dimensionId: "count",
    };
    const groupColumnBuilder: ColumnBuilder<any, any> = (
        extractor
    ): ColumnOptions<rp.EventWithRelations> =>
        toColumn(extractor, {
            getFormattedValue: (item) => {
                if (item.ancestors.length <= 0) {
                    return (
                        <span className={theme.aggregateSummaryLabel}>
                            {t("EventLogPage_Aggregate_Total")}
                        </span>
                    );
                } else {
                    return extractor.asString(item);
                }
            },
        });

    if (!data) {
        return <Skeleton />;
    }

    return (
        <ContentWithoutBorder>
            <AsyncTableFilter<rp.EventWithRelations>
                id="filter"
                queryResult={queryResult}
                dataViewIndex={dataViewIndex}
                filter={filter}
                hideResultCount={true}
                extractorRefs={getAllExtractors(dataView).map((ext) => ({
                    extractorId: ext.id,
                }))}
                onAddFilter={updateFilter}
                onRemoveFilter={removeFilter}
                onResetFilter={removeAllFilters}
                disabled={isFetching}
            >
                <DateFilterSelector
                    isLoading={false}
                    dateFilter={dateFilter}
                    onChangeDateFilter={reloadEventsBasedOnDate}
                />
            </AsyncTableFilter>
            <DeferredContent
                promise={queryResult}
                renderContent={(queryResult) => (
                    <TableDataViewer
                        id="events"
                        onSettingsChanged={reloadEventsBasedOnSettings}
                        groupColumnBuilder={groupColumnBuilder}
                        aggregateColumnBuilders={getAggregateColumnBuilders(
                            availableAggregates
                        )}
                        settings={tableSettings ?? undefined}
                        dataView={queryResult.dataView}
                        expanders={expanders}
                        availableAggregates={availableAggregates}
                        defaultAggregates={defaultAggregates}
                        columnBuilders={buildColumnsConfiguration()}
                        newExportMetadata={newExportMetadata}
                        // Remove tools while data is fetching, else the user can export and work with incomplete data
                        tools={
                            isFetching
                                ? {
                                      table: [],
                                      pivot: [],
                                      zoom: [],
                                  }
                                : undefined
                        }
                    />
                )}
            />
        </ContentWithoutBorder>
    );
};

function getDateFilter(startDate: moment.Moment, endDate: moment.Moment) {
    const dateFilter: DueDateFilter = {
        type: "DUE_DATE_FILTER",
        startDate,
        endDate,
    };

    return dateFilter;
}

function getTableSettings(
    tableSettingsParameter: string | undefined
): DataViewerSettings | null {
    return tableSettingsParameter ? JSON.parse(tableSettingsParameter) : null;
}

function buildDataView(
    events: rp.EventWithRelations[],
    indexedUsers: Record<number, UserView>,
    timezone: IanaTimezoneView
) {
    const formatEvent = (row: { event: rp.UserEvent }) =>
        formatUserEventString(row.event);
    const formatUser = (row: rp.EventWithRelations) =>
        formatUserName(indexedUsers[row.event.userId]);
    const formatUserWithSource = (row: rp.EventWithRelations) => {
        const source = eventSourceLabel(t, row.event.source);
        return source === null
            ? formatUser(row)
            : `${formatUser(row)} (${source})`;
    };
    const stringCompare = Cmp.string();
    const extractors: Array<DataExtractor<rp.EventWithRelations, any>> = [
        {
            id: EVENT_LOG_COLUMN.ID,
            label: "ID",
            getValue: (row) => row.event.id,
            compare: Cmp.map((row) => row.event.id, Cmp.primitive),
            asString: (row) => row.event.id.toString(),
        },
        {
            id: "date",
            label: t("EventLogPage_TableColumn_Date"),
            getValue: (row) => ({
                utcDate: new Date(row.event.created),
                timezone,
            }),
            compare: Cmp.map((row) => row.event.created, Cmp.primitive),
            asString: (row) => {
                return formatDateTimeAndTimezone(row.event.created, timezone);
            },
        },
        {
            id: "category",
            label: t("EventLogPage_TableColumn_Category"),
            getValue: (row) => row.event.resourceType,
            compare: Cmp.map((row) => row.event.resourceType, stringCompare),
            asString: (row) => row.event.resourceType,
        },
        {
            id: EVENT_LOG_COLUMN.TYPE,
            label: t("EventLogPage_TableColumn_Type"),
            getValue: (row) => row.event.name,
            compare: Cmp.map((row) => row.event.name, stringCompare),
            asString: (row) => row.event.name,
        },
        {
            id: "event",
            label: t("EventLogPage_TableColumn_Event"),
            getValue: formatEvent,
            compare: Cmp.map(formatEvent, stringCompare),
            asString: formatEvent,
        },
        {
            id: "user",
            label: t("EventLogPage_TableColumn_User"),
            getValue: formatUser,
            compare: Cmp.map((row) => row.event.userId, Cmp.primitive),
            asString: formatUserWithSource,
        },
        {
            id: "source",
            label: t("EventLogPage_TableColumn_Source"),
            getValue: (row) => row.event.resourceName,
            compare: Cmp.map((row) => row.event.resourceName, stringCompare),
            asString: (row) => row.event.resourceName,
        },
    ];
    const resourceTypeColumnNames = {
        [rp.ResourceType.USER]: t("EventLogPage_TableColumn_RelatedUsers"),
        [rp.ResourceType.USER_GROUP]: t(
            "EventLogPage_TableColumn_RelatedUserGroups"
        ),
        [rp.ResourceType.CONTROL_PROGRAM]: t(
            "EventLogPage_TableColumn_RelatedControlPrograms"
        ),
        [rp.ResourceType.CONTROL]: t(
            "EventLogPage_TableColumn_RelatedControls"
        ),
        [rp.ResourceType.RISK_DIRECTORY]: t(
            "EventLogPage_TableColumn_RelatedRiskMaps"
        ),
        [rp.ResourceType.ENTITY]: t("EventLogPage_TableColumn_RelatedEntities"),
        [rp.ResourceType.PUSH_REPORT]: t(
            "EventLogPage_TableColumn_RelatedPushReports"
        ),
        [rp.ResourceType.API_KEY]: t("EventLogPage_TableColumn_RelatedApiKeys"),
        [rp.ResourceType.TESTING_PROGRAM]: t(
            "EventLogPage_TableColumn_RelatedTestingPrograms"
        ),
        [rp.ResourceType.TESTING_CONTROL]: t(
            "EventLogPage_TableColumn_RelatedTestingControls"
        ),
        [rp.ResourceType.SCIM_USER]: t(
            "EventLogPage_TableColumn_RelatedScimUsers"
        ),
    };
    const getRelations =
        (type: UserEventResourceType) => (row: rp.EventWithRelations) => {
            return row.relations
                .filter((rel) => rel.resourceType === type)
                .sort((a, b) => a.resourceName.localeCompare(b.resourceName));
        };
    const relationsExtractors: Array<
        DataExtractor<rp.EventWithRelations, any>
    > = rp.RESOURCE_TYPES.map((type) => {
        return {
            id: `related${type}`,
            label: resourceTypeColumnNames[type],
            getValue: (row) => getRelations(type)(row),
            compare: Cmp.map(
                (row) =>
                    getRelations(type)(row)
                        .map((rel) => rel.resourceName)
                        .join(", "),
                stringCompare
            ),
            asString: (row) =>
                getRelations(type)(row)
                    .map((rel) => rel.resourceName)
                    .join(", "),
        };
    });
    const dataView: DataView<rp.EventWithRelations> = {
        dataSource: new ArrayDataSource(events, (row) =>
            row.event.id.toString()
        ),
        extractorGroups: [
            {
                id: "standard",
                label: "Standard",
                extractors,
            },
            {
                id: "relations",
                label: "Relations",
                extractors: relationsExtractors,
            },
        ],
        sorters: [
            {
                extractorId: "date",
                direction: "ASC",
            },
        ],
        defaultCompareItems: Cmp.map((row) => row.event.name, stringCompare),
    };

    return dataView;
}

function buildDataViewExpanders(): Array<ExtractorExpander<any, any>> {
    return rp.RESOURCE_TYPES.map((type) => ({
        extractorId: `related${type}`,
        expand: (rels) => {
            return rels.map((rel: any) => ({
                partialId: rel.resourceId,
                expandedValue: rel,
            }));
        },
        compare: Cmp.undefinedLast(
            Cmp.map((rel) => rel.resourceName, Cmp.string())
        ),
        asString: (rel) => (rel ? rel.resourceName : ""),
    }));
}

function buildQueryResult(
    dataView: DataView<rp.EventWithRelations>,
    dataViewIndex: AsyncDataViewIndex<rp.EventWithRelations>,
    filter: SimpleAndFilter | null
): Promise<QueryResult<rp.EventWithRelations>> {
    if (filter) {
        return dataViewIndex.find(filter);
    } else {
        return Promise.resolve({
            dataView,
            orResults: [],
            excludedItemIds: null,
        });
    }
}

function buildEmptyQueryResult(
    dataView: DataView<rp.EventWithRelations>
): Promise<QueryResult<rp.EventWithRelations>> {
    return Promise.resolve({
        dataView: {
            dataSource: new ArrayDataSource([], dataView.dataSource.getId),
            defaultCompareItems: dataView.defaultCompareItems,
            extractorGroups: dataView.extractorGroups,
            sorters: dataView.sorters,
        },
        orResults: [],
        excludedItemIds: null,
    });
}

function getAggregateColumnBuilders(
    availableAggregates: AggregatesConfiguration<rp.EventWithRelations>
) {
    const aggregateColumnBuilders: {
        [extractorId: string]: AggregateColumnBuilder<
            NodeDescriptor<Group<any, any>>,
            any
        >;
    } = {};

    availableAggregates.aggregates.forEach((aggregate) => {
        aggregateColumnBuilders[aggregate.aggregateId] = (
            groupExtractor,
            extractor
        ) => {
            return toColumn(extractor, {
                cellClassName: { type: "Static", value: theme.countColumn },
                getFormattedValue: (item) => {
                    if (item.ancestors.length <= 0) {
                        return (
                            <span className={theme.countColumnTotal}>
                                {extractor.asString(item)}
                            </span>
                        );
                    } else {
                        return extractor.asString(item);
                    }
                },
            });
        };
    });

    return aggregateColumnBuilders;
}

function buildColumnsConfiguration() {
    const columnBuilders: { [extractorId: string]: ColumnBuilder<any, any> } =
        {};
    columnBuilders[EVENT_LOG_COLUMN.ID] =
        (): ColumnOptions<rp.EventWithRelations> => ({
            display: "NEVER",
        });
    columnBuilders["date"] = (
        extractor
    ): ColumnOptions<rp.EventWithRelations> => ({
        getFormattedValue: (item) => (
            <DateFormatter
                dateInfo={{
                    utcDate: extractor.getValue(item).utcDate,
                    timezone: extractor.getValue(item).timezone,
                }}
                showTime={true}
            />
        ),
        layout: { fixedWidth: 150 },
    });
    columnBuilders["category"] = (): ColumnOptions<rp.EventWithRelations> => ({
        layout: { fixedWidth: 200 },
    });
    columnBuilders[EVENT_LOG_COLUMN.TYPE] =
        (): ColumnOptions<rp.EventWithRelations> => ({
            layout: { fixedWidth: 200 },
        });
    columnBuilders["event"] = (): ColumnOptions<rp.EventWithRelations> => ({
        getFormattedValue: (item) => formatUserEventReact(item.event),
    });
    columnBuilders["user"] = (
        extractor
    ): ColumnOptions<rp.EventWithRelations> => ({
        getFormattedValue: (item) => (
            <>
                {extractor.getValue(item)}
                {eventSourceTag(t, item.event.source)}
            </>
        ),
        layout: { fixedWidth: 200 },
    });
    columnBuilders["source"] = (): ColumnOptions<rp.EventWithRelations> => ({
        display: "DEFAULT_HIDDEN",
    });
    const resourceTypeFormatter: {
        [key: string]: (rel: UserEventRelatedResourceView) => React.ReactNode;
    } = {
        [rp.ResourceType.USER]: (rel) => rel.resourceName,
        [rp.ResourceType.USER_GROUP]: (rel) => rel.resourceName,
        [rp.ResourceType.CONTROL_PROGRAM]: (rel) => (
            <a
                href={buildControlProgramViewUrl(rel.resourceId)}
                target="_blank"
            >
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.CONTROL]: (rel) => (
            <a href={`${WebRoute.CONTROLS}/${rel.resourceId}`} target="_blank">
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.RISK_DIRECTORY]: (rel) => (
            <a href={`/risks/${rel.resourceId}`} target="_blank">
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.ENTITY]: (rel) => (
            <a href={`/entities/${rel.resourceId}`} target="_blank">
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.PUSH_REPORT]: (rel) => (
            <a
                href={`${LegacyRoute.REPORT_CONTROL_RESULT_STATUS_SAVED}/${rel.resourceId}`}
                target="_blank"
            >
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.API_KEY]: (rel) => rel.resourceName,
        [rp.ResourceType.TESTING_PROGRAM]: (rel) => (
            <a
                href={`${WebRoute.TESTING_PROGRAMS}/${rel.resourceId}`}
                target="_blank"
            >
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.TESTING_CONTROL]: (rel) => (
            <a
                href={`${WebRoute.TESTING_CONTROLS}/${rel.resourceId}`}
                target="_blank"
            >
                {rel.resourceName}
            </a>
        ),
        [rp.ResourceType.SCIM_USER]: (rel) => rel.resourceName,
    };
    rp.RESOURCE_TYPES.forEach((type) => {
        const formatter = resourceTypeFormatter[type];
        columnBuilders[`related${type}`] = (
            extractor
        ): ColumnOptions<rp.EventWithRelations> => ({
            display: "DEFAULT_HIDDEN",
            getFormattedValue: (item) => {
                const value: UserEventRelatedResourceView[] | undefined =
                    extractor.getValue(item);
                return (
                    value
                        ?.map(formatter)
                        .reduce((accu: React.ReactNode[] | null, elem) => {
                            return accu === null
                                ? [elem]
                                : [...accu, ", ", elem];
                        }, null) ?? undefined
                );
            },
        });
    });

    return columnBuilders;
}
